import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { User, LogOut, Home, ShoppingBag, Menu, X, ChevronDown } from 'lucide-react';
import styled from 'styled-components';

const Navbar = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAuthenticated, user } = useSelector(state => state.auth);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        setIsProfileMenuOpen(false);
        setIsMobileMenuOpen(false);
    };

    const getDashboardLink = () => {
        if (!user) return '/';
        switch (user.role) {
            case 'superAdmin': return '/superadmin';
            case 'canteenAdmin': return '/canteen-admin';
            case 'staff': return '/staff';
            default: return '/';
        }
    };

    return (
        <React.Fragment>
            <NavbarWrapper />
            <StyledNavbar>
                <StyledContainer>
                    <StyledLogo onClick={() => navigate('/')}>
                        <ShoppingBag size={24} />
                        OMNIPET
                    </StyledLogo>

                    <StyledNav>
                        <StyledNavLink onClick={() => navigate(getDashboardLink())}>
                            <Home size={20} />
                            Home
                        </StyledNavLink>
                        <StyledNavLink onClick={() => navigate('/app')}>
                            <ShoppingBag size={20} />
                            Download App
                        </StyledNavLink>
                        {isAuthenticated ? (
                            <ProfileMenuContainer>
                                <StyledProfileButton onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
                                    <User size={20} />
                                    {user?.name || 'Profile'}
                                    <ChevronDown size={16} />
                                </StyledProfileButton>
                                <StyledProfileMenu isOpen={isProfileMenuOpen}>
                                    <StyledProfileMenuItem onClick={() => {
                                        navigate('/profile');
                                        setIsProfileMenuOpen(false);
                                    }}>
                                        <User size={18} />
                                        My Profile
                                    </StyledProfileMenuItem>
                                    <StyledProfileMenuItem onClick={handleLogout}>
                                        <LogOut size={18} />
                                        Logout
                                    </StyledProfileMenuItem>
                                </StyledProfileMenu>
                            </ProfileMenuContainer>
                        ) : (
                            <React.Fragment>
                                <StyledNavLink onClick={() => navigate('/login')}>
                                    Login
                                </StyledNavLink>
                                <StyledRegisterButton onClick={() => navigate('/register')}>
                                    Register
                                </StyledRegisterButton>
                            </React.Fragment>
                        )}
                    </StyledNav>

                    <StyledMobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </StyledMobileMenuButton>
                </StyledContainer>
            </StyledNavbar>

            <StyledMobileNav isOpen={isMobileMenuOpen}>
                <StyledMobileNavLink onClick={() => {
                    navigate(getDashboardLink());
                    setIsMobileMenuOpen(false);
                }}>
                    <Home size={24} />
                    Home
                </StyledMobileNavLink>
                <StyledMobileNavLink onClick={() => {
                    navigate('/app');
                    setIsMobileMenuOpen(false);
                }}>
                    <ShoppingBag size={24} />
                    Download App
                </StyledMobileNavLink>
                {isAuthenticated ? (
                    <React.Fragment>
                        <StyledMobileNavLink onClick={() => {
                            navigate('/profile');
                            setIsMobileMenuOpen(false);
                        }}>
                            <User size={24} />
                            My Profile
                        </StyledMobileNavLink>
                        <StyledMobileNavLink onClick={handleLogout}>
                            <LogOut size={24} />
                            Logout
                        </StyledMobileNavLink>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <StyledMobileNavLink onClick={() => {
                            navigate('/login');
                            setIsMobileMenuOpen(false);
                        }}>
                            Login
                        </StyledMobileNavLink>
                        <StyledMobileNavLink onClick={() => {
                            navigate('/register');
                            setIsMobileMenuOpen(false);
                        }}>
                            Register
                        </StyledMobileNavLink>
                    </React.Fragment>
                )}
            </StyledMobileNav>
        </React.Fragment>
    );
};

export default Navbar;

const NavbarWrapper = styled.div`
    width: 100%;
    height: 64px;
    @media (max-width: 576px) {
        height: 56px;
    }
`;

const StyledNavbar = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 64px;
    background-color: #000;
    color: #fff;
    z-index: 50;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    @media (max-width: 576px) {
        height: 56px;
    }
`;

const StyledContainer = styled.div`
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    @media (max-width: 768px) {
        padding: 0 16px;
    }
`;

const StyledLogo = styled.div`
    font-size: 1.5rem;
    font-weight: 800;
    color: #fff;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    @media (max-width: 576px) {
        font-size: 1.25rem;
    }
`;

const StyledNav = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    @media (max-width: 768px) {
        display: none;
    }
`;

const StyledMobileNav = styled.div`
    display: none;
    @media (max-width: 768px) {
        display: ${props => (props.isOpen ? 'flex' : 'none')};
        position: fixed;
        top: 64px;
        left: 0;
        right: 0;
        background-color: #000;
        flex-direction: column;
        padding: 24px;
        z-index: 49;
        @media (max-width: 576px) {
            top: 56px;
        }
    }
`;

const StyledMobileMenuButton = styled.button`
    display: none;
    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        background-color: transparent;
        cursor: pointer;
        color: #fff;
    }
`;

const StyledNavLink = styled.button`
    color: #fff;
    font-size: 1rem;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const StyledMobileNavLink = styled.div`
    color: #fff;
    font-size: 1.125rem;
    font-weight: 500;
    padding: 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const ProfileMenuContainer = styled.div`
    position: relative;
`;

const StyledProfileButton = styled.button`
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    font-weight: 500;
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const StyledProfileMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    background-color: #000;
    border-radius: 12px;
    padding: 8px;
    min-width: 200px;
    display: ${props => (props.isOpen ? 'block' : 'none')};
`;

const StyledProfileMenuItem = styled.div`
    padding: 12px 16px;
    color: #fff;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const StyledRegisterButton = styled.button`
    background-color: #fff;
    color: #000;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    &:hover {
        background-color: rgba(255, 255, 255, 0.9);
    }
`;
